import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Check, ArrowLeft, Shield, Cpu, Package, ChevronRight, ChevronLeft, Minus, Plus, ZoomIn, X, Award, Truck, Info } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from '../components/ui/sonner';
import ServiceOptions from '../components/ServiceOptions';
import ProductRecommendations from '../components/ProductRecommendations';
import QuickAddAccessories from '../components/QuickAddAccessories';
import CameraStorageOptions from '../components/CameraStorageOptions';
import CameraMountOptions from '../components/CameraMountOptions';
import WaitlistForm from '../components/WaitlistForm';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;
const VISIOTECH_CDN = "https://d1x12lhh8s9nlj.cloudfront.net/images/productos/products";

// Helper to extract SKU from Visiotech URL
const extractSkuFromUrl = (url) => {
  if (!url) return null;
  const match = url.match(/products\/([A-Z0-9-]+)\//i);
  return match ? match[1] : null;
};

// Generate gallery URLs based on SKU
const generateGalleryUrls = (imageUrl) => {
  const sku = extractSkuFromUrl(imageUrl);
  if (!sku) return [];
  
  const gallery = [];
  gallery.push(`${VISIOTECH_CDN}/${sku}/${sku}.png`);
  for (let i = 1; i <= 8; i++) {
    gallery.push(`${VISIOTECH_CDN}/${sku}/detalle${i}.png`);
  }
  return gallery;
};

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart, loading } = useCart();
  
  const [product, setProduct] = useState(null);
  const [loadingProduct, setLoadingProduct] = useState(true);
  const [selectedColor, setSelectedColor] = useState('black');
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [loadedGalleryImages, setLoadedGalleryImages] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [servicesTotal, setServicesTotal] = useState(0);
  const [showServicesPanel, setShowServicesPanel] = useState(true);
  const [selectedAccessories, setSelectedAccessories] = useState([]);
  const [accessoriesTotal, setAccessoriesTotal] = useState(0);
  const [showAccessoriesPanel, setShowAccessoriesPanel] = useState(true);
  const [selectedStorage, setSelectedStorage] = useState(null);
  const [storageTotal, setStorageTotal] = useState(0);
  const [selectedMount, setSelectedMount] = useState(null);
  const [mountTotal, setMountTotal] = useState(0);

  // Check if product is a Hub or Kit (eligible for services)
  const isHubOrKit = useCallback(() => {
    if (!product) return false;
    const name = product.name.toLowerCase();
    return name.includes('hub') || name.includes('kit') || product.subcategory === 'hubs' || product.subcategory === 'kits';
  }, [product]);

  // Check if product is a camera (eligible for storage options)
  const isCamera = useCallback(() => {
    if (!product) return false;
    const name = product.name.toLowerCase();
    const category = product.category?.toLowerCase() || '';
    const subcategory = product.subcategory?.toLowerCase() || '';
    return (
      name.includes('cam') || 
      name.includes('camera') || 
      name.includes('turret') || 
      name.includes('bullet') || 
      name.includes('dome') ||
      subcategory === 'cameras' ||
      category.includes('video')
    );
  }, [product]);

  const handleServicesChange = useCallback((services, total, hasInstallationConfirmed) => {
    setSelectedServices(services);
    setServicesTotal(total);
  }, []);

  const handleAccessoriesChange = useCallback((accessories, total) => {
    setSelectedAccessories(accessories);
    setAccessoriesTotal(total);
  }, []);

  const handleStorageChange = useCallback((storage, total) => {
    setSelectedStorage(storage);
    setStorageTotal(total);
  }, []);

  const handleMountChange = useCallback((mount, total) => {
    setSelectedMount(mount);
    setMountTotal(total);
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await fetch(`${API}/products/${productId}`);
        if (response.ok) {
          const data = await response.json();
          setProduct(data);
          setSelectedColor(data.colors?.[0] || 'black');
        } else {
          console.log('Error:', 'Produit non trouvé');
          navigate('/shop');
        }
      } catch (error) {
        console.error('Failed to fetch product:', error);
        console.log('Error:', 'Erreur lors du chargement du produit');
      } finally {
        setLoadingProduct(false);
      }
    };
    fetchProduct();
  }, [productId, navigate]);

  // Load gallery images when color changes
  useEffect(() => {
    if (!product) return;
    
    const colorImageUrl = product.images?.[selectedColor] || product.image_url;
    const potentialUrls = generateGalleryUrls(colorImageUrl);
    
    const checkImages = async () => {
      const validImages = [];
      for (const url of potentialUrls) {
        try {
          const img = new Image();
          const loaded = await new Promise((resolve) => {
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = url;
          });
          if (loaded) {
            validImages.push(url);
          }
        } catch (error) {
          console.warn('Image failed to load:', url, error);
        }
      }
      setLoadedGalleryImages(validImages);
      setGalleryImages(validImages);
      setSelectedImageIndex(0);
    };
    
    checkImages();
    setImageError(false);
  }, [selectedColor, product]);

  const getCurrentImageUrl = () => {
    if (!product) return '';
    if (loadedGalleryImages.length > 0 && loadedGalleryImages[selectedImageIndex]) {
      return loadedGalleryImages[selectedImageIndex];
    }
    if (imageError) return product.image_url;
    return product.images?.[selectedColor] || product.image_url;
  };

  const isShoppable = product?.category === 'basic' || product?.category === 'video_basic';

  const handleAddToCart = async () => {
    if (!isShoppable || !product) return;
    
    setIsAdding(true);
    try {
      const servicesForApi = selectedServices.map(s => ({
        service_id: s.service_id,
        name: s.name,
        price: s.price,
        price_type: s.price_type,
        distance_km: s.distance_km || null,
        confirmed_2_years: s.confirmed_2_years || false
      }));
      
      const result = await addToCart(product.product_id, quantity, selectedColor, servicesForApi);
      
      if (result.requiresAuth) {
        // Use React Router navigation with simple state
        navigate('/auth', { state: { from: `/product/${product.product_id}` } });
        return;
      }
      
      if (result.error) {
        toast.error("Erreur lors de l'ajout au panier");
        return;
      }
      
      // Add accessories
      for (const accessory of selectedAccessories) {
        await addToCart(accessory.product_id, accessory.quantity, 'black', []);
      }
      
      // Add storage card if selected (for cameras)
      if (selectedStorage) {
        await addToCart(selectedStorage.product_id, selectedStorage.quantity, 'default', []);
      }

      // Add mount if selected (for cameras)
      if (selectedMount) {
        await addToCart(selectedMount.product_id, selectedMount.quantity, 'black', []);
      }

      setAdded(true);
      setSelectedAccessories([]);
      setAccessoriesTotal(0);
      setSelectedStorage(null);
      setStorageTotal(0);
      setSelectedMount(null);
      setMountTotal(0);
      setTimeout(() => setAdded(false), 2000);
    } catch (error) {
      console.error('Add to cart error:', error);
      toast.error("Erreur lors de l'ajout au panier");
    } finally {
      setIsAdding(false);
    }
  };

  const hasDiscount = product?.original_price && product?.price && product.original_price > product.price;
  const discountPercent = hasDiscount 
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;

  const totalPrice = product ? (product.price * quantity + servicesTotal + accessoriesTotal + storageTotal + mountTotal) : 0;

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'basic': return 'Baseline Alarme Sans Fil';
      case 'video_basic': return 'Baseline Vidéoprotection IA';
      case 'pro': return 'Gamme Superior';
      case 'video_pro': return 'Vidéo Pro';
      case 'ssi': return 'SSI Pro EN54';
      default: return category;
    }
  };

  const getCategoryPath = (category) => {
    switch (category) {
      case 'basic': return '/shop';
      case 'video_basic': return '/video';
      case 'pro': return '/pro';
      case 'video_pro': return '/video-pro';
      case 'ssi': return '/ssi';
      default: return '/shop';
    }
  };

  if (loadingProduct) {
    return (
      <div className="min-h-screen py-8 md:py-12">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12">
          <div className="animate-pulse">
            <div className="h-6 bg-fgs-surface rounded w-48 mb-6" />
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-7 aspect-square bg-fgs-surface rounded-2xl" />
              <div className="lg:col-span-5 space-y-4">
                <div className="h-10 bg-fgs-surface rounded w-3/4" />
                <div className="h-6 bg-fgs-surface rounded w-1/2" />
                <div className="h-16 bg-fgs-surface rounded" />
                <div className="h-12 bg-fgs-surface rounded w-1/3" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen py-12 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Produit non trouvé</h1>
          <Link to="/shop">
            <Button className="bg-fgs-primary hover:bg-fgs-primary-hover">
              Retour à la boutique
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-zinc-950 pb-40 md:pb-24 lg:pb-12" data-testid="product-detail-page">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
          
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm mb-6" data-testid="breadcrumb">
            <Link to="/" className="text-fgs-text-muted hover:text-white transition-colors">Accueil</Link>
            <ChevronRight className="w-4 h-4 text-fgs-text-muted" />
            <Link to={getCategoryPath(product.category)} className="text-fgs-text-muted hover:text-white transition-colors">
              {getCategoryLabel(product.category)}
            </Link>
            <ChevronRight className="w-4 h-4 text-fgs-text-muted" />
            <span className="text-zinc-300 truncate max-w-[200px]">{product.name}</span>
          </nav>

          {/* Back Button */}
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-fgs-text-muted hover:text-white transition-colors mb-6"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Retour</span>
          </button>

          {/* Main Product Grid - 2 columns on desktop */}
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
            
            {/* LEFT COLUMN - Image Gallery (7 cols) */}
            <div className="lg:col-span-7 space-y-4">
              {/* Main Image */}
              <div 
                className="relative bg-zinc-900/50 border border-zinc-800/30 rounded-2xl overflow-hidden cursor-zoom-in group"
                onClick={() => setLightboxOpen(true)}
              >
                {/* Subtle radial glow behind product */}
                <div className="absolute inset-0 bg-gradient-radial from-emerald-500/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                {/* Badges */}
                <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
                  {/* Custom badge (bestseller, new, promo, etc.) */}
                  {product.badge && (
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${
                      product.badge === 'bestseller' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                      product.badge === 'new' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                      product.badge === 'promo' ? 'bg-red-500/10 text-red-400 border-red-500/20' :
                      product.badge === 'limited' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' :
                      product.badge === 'exclusive' ? 'bg-fuchsia-500/10 text-fuchsia-400 border-fuchsia-500/20' :
                      'bg-zinc-500/10 text-fgs-text-muted border-zinc-500/20'
                    }`}>
                      {product.badge === 'bestseller' ? 'Best Seller' :
                       product.badge === 'new' ? 'Nouveau' :
                       product.badge === 'promo' ? 'Promo' :
                       product.badge === 'limited' ? 'Édition Limitée' :
                       product.badge === 'exclusive' ? 'Exclusif' :
                       product.badge}
                    </span>
                  )}
                  {/* Discount badge */}
                  {hasDiscount && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                      -{discountPercent}%
                    </span>
                  )}
                  {/* Coming Soon badge */}
                  {product.coming_soon && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-500/40">
                      🔜 Bientôt disponible
                    </span>
                  )}
                  {/* AI badge */}
                  {product.ai_enabled && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-blue-500/10 text-blue-400 border border-blue-500/20">
                      <Cpu className="w-3 h-3" />
                      IA
                    </span>
                  )}
                </div>

                {/* Zoom icon */}
                <div className="absolute bottom-4 right-4 z-10 bg-black/60 p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                  <ZoomIn className="w-5 h-5 text-white" />
                </div>

                {/* Navigation arrows for gallery */}
                {loadedGalleryImages.length > 1 && (
                  <>
                    <button
                      className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-black/80 p-2 rounded-full transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImageIndex((prev) => (prev === 0 ? loadedGalleryImages.length - 1 : prev - 1));
                      }}
                    >
                      <ChevronLeft className="w-5 h-5 text-white" />
                    </button>
                    <button
                      className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-black/80 p-2 rounded-full transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImageIndex((prev) => (prev === loadedGalleryImages.length - 1 ? 0 : prev + 1));
                      }}
                    >
                      <ChevronRight className="w-5 h-5 text-white" />
                    </button>
                  </>
                )}

                <div className="aspect-square flex items-center justify-center p-8 md:p-12">
                  <img
                    src={getCurrentImageUrl()}
                    alt={`${product.name} - ${selectedColor === 'black' ? 'Noir' : 'Blanc'}`}
                    className="max-w-full max-h-full object-contain transition-transform group-hover:scale-105"
                    onError={(e) => {
                      if (!imageError) {
                        setImageError(true);
                        e.target.src = product.image_url;
                      }
                    }}
                    data-testid="product-image"
                  />
                </div>
              </div>

              {/* Gallery Thumbnails */}
              {loadedGalleryImages.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-zinc-700">
                  {loadedGalleryImages.map((imgUrl, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg border-2 p-1 bg-zinc-900 transition-all ${
                        selectedImageIndex === index
                          ? 'border-fgs-primary ring-2 ring-fgs-primary/30'
                          : 'border-zinc-700 hover:border-zinc-500'
                      }`}
                      data-testid={`gallery-thumb-${index}`}
                    >
                      <img src={imgUrl} alt={`Vue ${index + 1}`} className="w-full h-full object-contain" />
                    </button>
                  ))}
                </div>
              )}

              {/* Description - Desktop only, below gallery */}
              <div className="hidden lg:block pt-10 mt-6 border-t border-zinc-800">
                {product.description_long && (
                  <div className="mb-16">
                    <h3 className="text-xl font-semibold text-white flex items-center gap-3 mb-5">
                      <Info className="w-6 h-6 text-fgs-primary" />
                      Description
                    </h3>
                    <p className="text-fgs-text-muted leading-relaxed text-base">{product.description_long}</p>
                  </div>
                )}

                {/* Features */}
                {product.features && product.features.length > 0 && (
                  <div className="mb-16">
                    <h3 className="text-xl font-semibold text-white flex items-center gap-3 mb-5">
                      <Check className="w-6 h-6 text-fgs-primary" />
                      Caractéristiques clés
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      {product.features.map((feature, i) => (
                        <div key={i} className="flex items-start gap-3 bg-zinc-900/50 rounded-lg p-4">
                          <div className="w-6 h-6 rounded-full bg-fgs-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-4 h-4 text-fgs-primary" />
                          </div>
                          <span className="text-zinc-300">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Specs */}
                {product.specs && Object.keys(product.specs).length > 0 && (
                  <div>
                    <h3 className="text-xl font-semibold text-white flex items-center gap-3 mb-5">
                      <Cpu className="w-6 h-6 text-fgs-primary" />
                      Spécifications techniques
                    </h3>
                    <div className="bg-zinc-900/50 rounded-xl overflow-hidden border border-zinc-800">
                      {Object.entries(product.specs).map(([key, value], i) => (
                        <div 
                          key={key} 
                          className={`flex justify-between items-center px-5 py-4 ${
                            i % 2 === 0 ? 'bg-zinc-800/30' : ''
                          } ${i !== Object.keys(product.specs).length - 1 ? 'border-b border-zinc-800/50' : ''}`}
                        >
                          <span className="text-fgs-text-muted">{key}</span>
                          <span className="text-white font-medium text-right">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT COLUMN - Product Info & Actions (5 cols) - STICKY */}
            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-24 space-y-8">
                
                {/* Category Badge */}
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  <Shield className="w-3 h-3 mr-1.5" />
                  {getCategoryLabel(product.category)}
                </span>

                {/* Product Name */}
                <div className="space-y-4">
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight" data-testid="product-name">
                    {product.name}
                  </h1>

                  {/* Short Description */}
                  <p className="text-fgs-text-muted text-base leading-relaxed">{product.description}</p>
                </div>

                {/* Guarantee & Stock Badges */}
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-zinc-800/80 text-zinc-300 border border-zinc-700">
                    <Award className="w-3 h-3 mr-1.5 text-emerald-400" />
                    Garantie {product.guarantee_years || 4} ans
                  </span>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-zinc-800/80 text-zinc-300 border border-zinc-700">
                    <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${product.in_stock !== false ? 'bg-emerald-400' : 'bg-red-400'}`} />
                    {product.in_stock !== false ? 'En stock' : 'Rupture'}
                  </span>
                </div>

                {/* Purchase Card - Glassmorphic container */}
                <div className="p-6 rounded-2xl bg-zinc-900/40 border border-zinc-800/60 backdrop-blur-sm">
                  
                  {/* Price Section */}
                  {product.price ? (
                    <div className="space-y-3 pb-6">
                      <div className="flex items-baseline gap-3">
                        <span className="font-mono text-3xl sm:text-4xl font-bold text-white tracking-tight" data-testid="product-price">
                          {product.price.toFixed(2)} €
                        </span>
                        {hasDiscount && (
                          <span className="font-mono text-lg text-fgs-text-muted line-through decoration-zinc-600">
                            {product.original_price.toFixed(2)} €
                          </span>
                        )}
                      </div>
                      
                      {hasDiscount && (
                        <p className="text-emerald-400 text-sm font-medium">
                          Économisez {(product.original_price - product.price).toFixed(2)} €
                        </p>
                      )}

                      <p className="text-white/60 text-xs">Prix TTC • Livraison gratuite dès 250€</p>

                      {/* Klarna 3x Payment */}
                      {product.price >= 35 && (
                        <div className="flex items-center gap-3 mt-3 p-3 bg-[#FFB3C7]/10 border border-[#FFB3C7]/20 rounded-xl">
                          <div className="w-10 h-10 bg-[#FFB3C7] rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-black font-black text-sm">K.</span>
                          </div>
                          <div>
                            <p className="text-white text-sm font-medium">
                              ou <span className="text-[#FFB3C7] font-bold">{(product.price / 3).toFixed(2)} €</span> × 3 avec Klarna
                            </p>
                            <p className="text-white/50 text-xs">Paiement en 3 fois sans frais</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="pb-6">
                      <span className="text-2xl text-emerald-400 font-medium">Sur devis</span>
                    </div>
                  )}

                  {/* Divider after price */}
                  <div className="border-t border-zinc-800/60 mb-6" />

                  {/* Color Selector */}
                  {product.colors && product.colors.length > 1 && (
                    <div className="space-y-3 pb-6">
                      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-fgs-text-muted">Couleur</span>
                      <div className="flex items-center gap-3">
                        {product.colors.map((color) => (
                          <button
                            key={color}
                            onClick={() => setSelectedColor(color)}
                            className={`relative w-8 h-8 rounded-full border transition-all ${
                              selectedColor === color
                                ? 'ring-2 ring-offset-2 ring-offset-zinc-950 ring-emerald-500 border-transparent'
                                : 'border-zinc-700 hover:border-zinc-500'
                            }`}
                            data-testid={`color-select-${color}`}
                          >
                            <span className={`absolute inset-0.5 rounded-full ${
                              color === 'black' ? 'bg-zinc-900' : 'bg-zinc-100'
                            }`} />
                          </button>
                        ))}
                        <span className="text-fgs-text-muted text-sm ml-2">{selectedColor === 'black' ? 'Noir' : 'Blanc'}</span>
                      </div>
                    </div>
                  )}

                  {isShoppable && (
                    <>
                      {/* Service Options for Hub/Kit */}
                      {isHubOrKit() && (
                        <div className="bg-zinc-950/50 border border-zinc-800/60 rounded-xl overflow-hidden mb-6">
                          <button
                            onClick={() => setShowServicesPanel(!showServicesPanel)}
                            className="w-full p-4 flex items-center justify-between hover:bg-zinc-800/30 transition-colors"
                            data-testid="toggle-services"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                                <Truck className="w-5 h-5 text-emerald-400" />
                              </div>
                              <div className="text-left">
                                <p className="text-white font-medium text-sm">Services optionnels</p>
                                <p className="text-fgs-text-muted text-xs mt-0.5">Installation, 4G, Programmation</p>
                              </div>
                            </div>
                            <ChevronRight className={`w-4 h-4 text-fgs-text-muted transition-transform ${showServicesPanel ? 'rotate-90' : ''}`} />
                          </button>
                          
                          {showServicesPanel && (
                            <div className="px-4 pb-4 border-t border-zinc-800/60 pt-4">
                              <ServiceOptions onServicesChange={handleServicesChange} showTitle={false} />
                            </div>
                          )}
                        </div>
                      )}

                      {/* Quick Add Accessories for Hub/Kit */}
                      {isHubOrKit() && (
                        <div className="bg-zinc-950/50 border border-zinc-800/60 rounded-xl overflow-hidden mb-6">
                          <button
                            onClick={() => setShowAccessoriesPanel(!showAccessoriesPanel)}
                            className="w-full p-4 flex items-center justify-between hover:bg-zinc-800/30 transition-colors"
                            data-testid="toggle-accessories"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                                <Package className="w-5 h-5 text-purple-400" />
                              </div>
                              <div className="text-left">
                                <p className="text-white font-medium text-sm">Ajouter des accessoires</p>
                                <p className="text-fgs-text-muted text-xs mt-0.5">Détecteurs, sirènes, claviers...</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {accessoriesTotal > 0 && (
                                <span className="text-xs text-purple-400 font-medium">+{accessoriesTotal.toFixed(2)}€</span>
                              )}
                              <ChevronRight className={`w-4 h-4 text-fgs-text-muted transition-transform ${showAccessoriesPanel ? 'rotate-90' : ''}`} />
                            </div>
                          </button>
                          
                          {showAccessoriesPanel && (
                            <div className="px-4 pb-4 border-t border-zinc-800/60 pt-4">
                              <QuickAddAccessories onAccessoriesChange={handleAccessoriesChange} />
                            </div>
                          )}
                        </div>
                      )}

                      {/* Camera Storage Options */}
                      {isCamera() && (
                        <div className="bg-zinc-950/50 border border-zinc-800/60 rounded-xl p-4 mb-6">
                          <CameraStorageOptions 
                            onStorageChange={handleStorageChange} 
                            productCategory={product?.category}
                          />
                        </div>
                      )}

                      {/* Camera Mount Options (MountCam) */}
                      {isCamera() && (
                        <div className="bg-zinc-950/50 border border-zinc-800/60 rounded-xl p-4 mb-6">
                          <CameraMountOptions
                            onMountChange={handleMountChange}
                            productName={product?.name}
                          />
                        </div>
                      )}

                      {/* TVA 10% Promo - Alarmes uniquement */}
                      {isHubOrKit() && (
                        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4 mb-6">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                              <Shield className="w-4 h-4 text-emerald-400" />
                            </div>
                            <div>
                              <p className="text-emerald-400 font-medium text-sm">TVA à 10% avec installation</p>
                              <p className="text-fgs-text-muted text-xs mt-1">
                                Pour les logements de +2 ans • <span className="text-amber-400">Installation disponible pour les produits alarme uniquement</span>
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Camera installation notice */}
                      {isCamera() && (
                        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 mb-6">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                              <Info className="w-4 h-4 text-amber-400" />
                            </div>
                            <div>
                              <p className="text-amber-400 font-medium text-sm">Installation non disponible</p>
                              <p className="text-fgs-text-muted text-xs mt-1">
                                L'installation par nos techniciens est réservée aux systèmes d'alarme Ajax. 
                                Pour la vidéosurveillance, veuillez <Link to="/contact" className="text-emerald-400 hover:underline">nous contacter</Link> pour un devis personnalisé.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Divider before quantity */}
                      <div className="border-t border-zinc-800/60 mb-6" />

                      {/* Quantity Selector */}
                      <div className="flex items-center justify-between mb-6">
                        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-fgs-text-muted">Quantité</span>
                        <div className="flex items-center border border-zinc-800 rounded-xl p-1 bg-zinc-950/50">
                          <button
                            onClick={() => setQuantity(Math.max(1, quantity - 1))}
                            className="w-10 h-10 flex items-center justify-center text-fgs-text-muted hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
                            data-testid="quantity-minus"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-12 text-center text-lg font-bold text-white font-mono" data-testid="quantity-value">
                            {quantity}
                          </span>
                          <button
                            onClick={() => setQuantity(quantity + 1)}
                            className="w-10 h-10 flex items-center justify-center text-fgs-text-muted hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
                            data-testid="quantity-plus"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Total with Services & Accessories Summary */}
                      {(selectedServices.length > 0 || selectedAccessories.length > 0) && (
                        <div className="bg-zinc-950/50 border border-zinc-800/60 rounded-xl p-4 space-y-2 mb-6">
                          <div className="flex justify-between text-sm">
                            <span className="text-fgs-text-muted">Produit × {quantity}</span>
                            <span className="text-white font-mono">{(product.price * quantity).toFixed(2)} €</span>
                          </div>
                          {selectedAccessories.map((accessory, i) => (
                            <div key={`acc-${i}`} className="flex justify-between text-sm">
                              <span className="text-purple-400 truncate mr-2">{accessory.name} × {accessory.quantity}</span>
                              <span className="text-white whitespace-nowrap font-mono">
                                {(accessory.price * accessory.quantity).toFixed(2)} €
                              </span>
                            </div>
                          ))}
                          {selectedServices.map((service, i) => (
                            <div key={`srv-${i}`} className="flex justify-between text-sm">
                              <span className="text-fgs-text-muted truncate mr-2">{service.name}</span>
                              <span className="text-white whitespace-nowrap font-mono">
                                {service.price.toFixed(2)} €
                                {service.price_type === 'monthly' && '/mois'}
                                {service.price_type === 'annual' && '/an'}
                              </span>
                            </div>
                          ))}
                          <div className="flex justify-between pt-2 border-t border-zinc-800/60">
                            <span className="text-white font-medium">Total</span>
                            <span className="text-emerald-400 font-mono font-bold text-lg">
                              {totalPrice.toFixed(2)} €
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Add to Cart Button - Desktop (or Waitlist form if coming_soon) */}
                      {product?.coming_soon ? (
                        <div className="hidden lg:block">
                          <WaitlistForm
                            productId={product.product_id}
                            productName={product.name}
                          />
                        </div>
                      ) : (
                      <Button
                        onClick={handleAddToCart}
                        disabled={isAdding || loading}
                        className="hidden lg:flex w-full py-4 h-auto rounded-xl text-lg font-bold transition-all hover:scale-[1.02] active:scale-[0.98] bg-emerald-500 hover:bg-emerald-400 text-zinc-950"
                        data-testid="add-to-cart-btn"
                      >
                        {added ? (
                          <>
                            <Check className="w-5 h-5 mr-2" />
                            Ajouté au panier
                          </>
                        ) : (
                          <>
                            <ShoppingCart className="w-5 h-5 mr-2" />
                            Ajouter au panier — {totalPrice.toFixed(2)} €
                          </>
                        )}
                      </Button>
                      )}
                    </>
                  )}

                  {!isShoppable && (
                    <Link to={`/${product.category}#quote`}>
                      <Button 
                        className="w-full py-4 h-auto rounded-xl text-lg font-bold bg-emerald-500 hover:bg-emerald-400 text-zinc-950"
                      >
                        <Package className="w-5 h-5 mr-2" />
                        Demander un devis
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Description & Specs */}
          <div className="lg:hidden mt-10 pt-10 border-t border-zinc-800">
            {product.description_long && (
              <div className="mb-12">
                <h3 className="text-xl font-semibold text-white mb-4">Description</h3>
                <p className="text-fgs-text-muted leading-relaxed">{product.description_long}</p>
              </div>
            )}

            {product.features && product.features.length > 0 && (
              <div className="mb-12">
                <h3 className="text-xl font-semibold text-white mb-4">Caractéristiques</h3>
                <div className="space-y-3">
                  {product.features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-3 bg-zinc-900/50 rounded-lg p-3">
                      <Check className="w-5 h-5 text-fgs-primary flex-shrink-0 mt-0.5" />
                      <span className="text-zinc-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {product.specs && Object.keys(product.specs).length > 0 && (
              <div>
                <h3 className="text-xl font-semibold text-white mb-4">Spécifications</h3>
                <div className="bg-zinc-900/30 rounded-xl overflow-hidden">
                  {Object.entries(product.specs).map(([key, value], i) => (
                    <div key={key} className={`flex justify-between py-3 px-4 ${i % 2 === 0 ? 'bg-zinc-800/20' : ''} ${i !== Object.keys(product.specs).length - 1 ? 'border-b border-zinc-800/50' : ''}`}>
                      <span className="text-fgs-text-muted">{key}</span>
                      <span className="text-white font-medium">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Product Recommendations */}
          {product && (
            <div className="mt-16 pt-10 border-t border-zinc-800">
              <ProductRecommendations currentProduct={product} maxItems={4} />
            </div>
          )}
        </div>
      </div>

      {/* MOBILE FIXED BOTTOM BAR */}
      {isShoppable && product?.price && (
        <div className="lg:hidden fixed bottom-12 left-0 right-0 z-[100] bg-zinc-950 backdrop-blur-xl border-t border-zinc-800/60 px-4 py-3">
          <div className="flex items-center gap-3 max-w-[500px] mx-auto">
            <div className="flex-1 min-w-0">
              <p className="font-mono text-xl font-bold text-white tracking-tight">{totalPrice.toFixed(2)} €</p>
              {selectedServices.length > 0 && (
                <p className="text-[10px] text-fgs-text-muted truncate">+ {selectedServices.length} service(s)</p>
              )}
            </div>
            <Button
              onClick={handleAddToCart}
              disabled={isAdding || loading || product?.coming_soon}
              className={`h-11 px-5 rounded-xl font-bold transition-all active:scale-95 ${
                product?.coming_soon
                  ? 'bg-amber-500/30 text-amber-200 cursor-not-allowed'
                  : added 
                    ? 'bg-emerald-500 text-zinc-950' 
                    : 'bg-emerald-500 hover:bg-emerald-400 text-zinc-950'
              }`}
              data-testid="mobile-add-to-cart-btn"
            >
              {product?.coming_soon ? (
                <>🔜 Bientôt</>
              ) : added ? (
                <>
                  <Check className="w-4 h-4 mr-1.5" />
                  Ajouté
                </>
              ) : (
                <>
                  <ShoppingCart className="w-4 h-4 mr-1.5" />
                  Ajouter
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <button 
            className="absolute top-4 right-4 text-white/70 hover:text-white p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            onClick={() => setLightboxOpen(false)}
          >
            <X className="w-6 h-6" />
          </button>

          {loadedGalleryImages.length > 1 && (
            <>
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImageIndex((prev) => (prev === 0 ? loadedGalleryImages.length - 1 : prev - 1));
                }}
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImageIndex((prev) => (prev === loadedGalleryImages.length - 1 ? 0 : prev + 1));
                }}
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}

          <img
            src={getCurrentImageUrl()}
            alt={product.name}
            className="max-w-full max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {loadedGalleryImages.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-black/50 p-2 rounded-lg">
              {loadedGalleryImages.map((imgUrl, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImageIndex(index);
                  }}
                  className={`w-10 h-10 rounded border-2 p-0.5 transition-all ${
                    selectedImageIndex === index
                      ? 'border-fgs-primary'
                      : 'border-transparent hover:border-white/50'
                  }`}
                >
                  <img src={imgUrl} alt={`Vue ${index + 1}`} className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          )}

          <div className="absolute top-4 left-4 text-white/70 bg-black/50 px-3 py-1 rounded-lg text-sm">
            {selectedImageIndex + 1} / {loadedGalleryImages.length}
          </div>
        </div>
      )}
    </>
  );
};

export default ProductDetailPage;
